import './MaintenanceScreen.scss';

import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { fromJS, List, Map, Set } from 'immutable';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import UndoIcon from '@mui/icons-material/UndoSharp';
import UnitSelector from 'src/component/UI/UnitSelector';
import CheckCircleIcon from '@mui/icons-material/CheckSharp';
import ValidateIcon from '@mui/icons-material/BorderColorSharp';
import AlertIcon from '@mui/icons-material/ReportProblemSharp';
import Button from 'src/component/UI/Button';
import FloatingActionBar from 'src/component/UI/FloatingActionBar/FloatingActionBar';
import NotificationNumberModal from './NotificationNumberModal';
import BulkValidateModal from './BulkValidateModal';
import AlarmNoteModal from './AlarmNoteModal';
import { formatAlarmLabel } from 'src/component/AlarmsList';
import DataTable from 'src/component/UI/DataTable';
import SettingsModal from 'src/component/UI/SettingsModal/SettingsModal';
import { SettingsModalTypes } from 'src/component/UI/SettingsModal/SettingsModalTypes';

import { getSettingsModal } from 'src/module/device/selector';
import { setSettingsModalOpen, setSettingsModalClose } from 'src/module/device/action';
import { fetchAlarmStats, fetchDevicesInAlarmWithoutNotification, validateAlarms, dismissAlarms, undoAlarms, dismissAlarmsByGroup } from 'src/module/alarm/action';
import { getDevicesInAlarmWithoutNotification, getDevicesInAlarmLoading, getAlarmStats, getCriticalAlarmsWithoutNotification } from 'src/module/alarm/selector';
import { getIntegrityChecks } from 'src/module/facility/selector';
import { getDevicesSelectedFilters, getIsAllFiltersEmpty } from 'src/module/filter/selector';
import { fetchIntegrityChecks } from "src/module/facility/action";
import { getPluralWorkTicketLabel, getSingularWorkTicketLabel, getCriticalityLabels, getTemperatureUnit } from 'src/module/authentication/selector';
import { celsiusToFahrenheitConvertor, formatDate, isWhitelabel, useAlarmReports } from 'src/utils/utils';
import { Tooltip } from '@mui/material';
import LoadingPlaceholder from 'src/component/UI/LoadingPlaceholder';
import PermissionFence from 'src/component/PermissionFence';
import toast from 'src/utils/toast';
import { NavLink } from 'react-router-dom';
import variables from 'src/style/variable/variables.module.scss';
import useMediaQuery from '@mui/material/useMediaQuery';
import DeviceRotate from 'src/component/UI/DeviceRotate';
import DeviceLinkCombo from 'src/component/UI/DeviceLinkCombo';
import { useTranslation } from 'src/i18n';

export default function MaintenanceScreen () {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const settingsModal = useSelector(getSettingsModal(null));
  const criticalAlarmsWithoutNotification = useSelector(getCriticalAlarmsWithoutNotification);
  const isAllFiltersEmpty = useSelector(getIsAllFiltersEmpty);
  const devicesInAlarmWithoutNotification = useSelector(getDevicesInAlarmWithoutNotification);
  const integrityChecks = useSelector(getIntegrityChecks);
  const alarmStats = useSelector(getAlarmStats);
  const criticalityLabels = useSelector(getCriticalityLabels);
  const loading = useSelector(getDevicesInAlarmLoading);
  const pluralWorkTicketLabel = useSelector(getPluralWorkTicketLabel);
  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);
  const temperatureUnit = useSelector(getTemperatureUnit);
  const selectedFilters = useSelector(getDevicesSelectedFilters);

  const [activeRowId, setActiveRowId] = useState(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [bulkValidateModalOpen, setBulkValidateModalOpen] = useState(false);
  const [alarmNoteModalOpen, setAlarmNoteModalOpen] = useState(false);
  const [alarms, setAlarms] = useState(List());
  const [controllerId, setControllerId] = useState(null);
  const [circuitId, setCircuitId] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [devicesInAlarmWithoutNotificationByCriticality, setDevicesInAlarmWithoutNotificationByCriticality] = useState(List());
  const [alarmGroupCollapsed, setAlarmGroupCollapsed] = useState(List());
  const [hasActionOccurred, setHasActionOccurred] = useState(false);
  const [criticality, setCriticality] = useState(null);
  const [criticalitylabel, setCriticalityLabel] = useState('');
  const [alarmGroupOptions, setAlarmGroupOptions] = useState([{ label: '', value: '' }]);
  const [useAlarmReportsValue, setUseAlarmReportsValue] = useState<boolean | null>(null);
  const [useAlarmReportsLoading, setUseAlarmReportsLoading] = useState(true);

  // Multi-select state - keyed by criticality level
  const [selectedRows, setSelectedRows] = useState<Map<number, Set<string>>>(Map());
  const selectedRowsRef = useRef(selectedRows);
  selectedRowsRef.current = selectedRows;

  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [showRotateMessage, setShowRotateMessage] = useState(isMobile && isPortrait);

  useEffect(() => {
    setShowRotateMessage(isMobile && isPortrait);
  }, [isMobile, isPortrait]);

  useEffect(() => {
    async function loadData () {
      const result = await useAlarmReports();
      setUseAlarmReportsValue(result);
      setUseAlarmReportsLoading(false);
    }
    loadData();
  }, []);

  const criticalities = [];

  for (let i = criticalityLabels.size - 1; i >= 0; i--) {
    criticalities.push({ label: criticalityLabels.get(i), value: i });
  }

  useEffect(() => {
    // init alarm group collapse state
    const alarmGroupCollapsedArray = [true];

    for (let i = 1; i < criticalityLabels.size; i++) {
      alarmGroupCollapsedArray[i] = false;
    }

    setAlarmGroupCollapsed(List(alarmGroupCollapsedArray));
  }, [criticalityLabels]);

  useEffect(() => {
    const optionalFilters = {
      controllerId,
      deviceId
    };

    dispatch(fetchAlarmStats(selectedFilters, Map(optionalFilters)));
    dispatch(fetchIntegrityChecks(selectedFilters));
    dispatch(fetchDevicesInAlarmWithoutNotification(selectedFilters, Map(optionalFilters)));
  }, [selectedFilters]);

  useEffect(() => {
    if (!devicesInAlarmWithoutNotification) {
      return;
    }

    const grouped = [];
    devicesInAlarmWithoutNotification.forEach((device) => {
      const criticality = device.get('criticality');
      if (!grouped[criticality]) {
        grouped[criticality] = List();
      }

      grouped[criticality] = grouped[criticality].push(device);
    });

    setDevicesInAlarmWithoutNotificationByCriticality(List(grouped));
  }, [devicesInAlarmWithoutNotification]);

  const toggleNotificationModal = (rowId=null, alarms=null, deviceId=null) => {
    setNotificationModalOpen(!notificationModalOpen);
    setActiveRowId(rowId);
    setDeviceId(deviceId);
    setAlarms(alarms);
  };

  const handleNotificationSubmit = () => {
    // Note: additionalDeviceIds is collected by the UI but not yet passed to the API
    return (notificationNumber, description) => {
      const alarmIds = [];

      alarms.forEach((alarm: any) => {
        alarmIds.push(alarm.get('id'));
      });
      dispatch(validateAlarms(notificationNumber, alarmIds, description, activeRowId, selectedFilters, deviceId));
      toggleNotificationModal();
      setHasActionOccurred(true);
    };
  };

  const makeToggleAlarmNoteModal = (rowId=null, alarms=null, controllerId=null, circuitId=null, deviceId=null) => {
    return () => {
      toggleAlarmNoteModal(rowId, alarms, controllerId, circuitId, deviceId);
    };
  };
  const undo = (rowId=null, alarms=null, controllerId=null, circuitId=null) => {
    const alarmIds = [];

    alarms.forEach((alarm: any) => {
      alarmIds.push(alarm.get('id'));
    });
    dispatch(undoAlarms(controllerId, circuitId, alarmIds, rowId));
  };

  const toggleAlarmNoteModal = (rowId=null, alarms=null, controllerId=null, circuitId=null, deviceId=null) => {
    setAlarmNoteModalOpen(!alarmNoteModalOpen);
    setActiveRowId(rowId);
    setControllerId(controllerId);
    setCircuitId(circuitId);
    setDeviceId(deviceId);
    setAlarms(alarms);
  };

  const handleAlarmNote = () => (alarmNote, expiry, subject) => {
    const alarmIds = [];

    alarms.forEach((alarm: any) => {
      alarmIds.push(alarm.get('id'));
    });
    const pageInfo = {};
    dispatch(dismissAlarms(alarmNote, controllerId, circuitId, alarmIds, expiry, subject, selectedFilters, pageInfo, activeRowId, deviceId));

    toggleAlarmNoteModal();
    setHasActionOccurred(true);
  };


  const noteSummaryData = [
    { label: t('maintenance.nuisanceAlarms'), value: alarmStats.getIn(['notesByHtc', 'nuisance'], 'N/A') },
    { label: t('maintenance.lotoCircuits'), value: alarmStats.getIn(['notesByHtc', 'loto'], 'N/A') },
    { label: t('maintenance.offlineCircuits'), value: alarmStats.getIn(['notesByHtc', 'offline'], 'N/A') },
    { label: t('common.other'), value: alarmStats.getIn(['notesByHtc', 'other'], 'N/A') },
  ];

  const integrityChecksData = [
    { label: t('maintenance.alarmsWithoutSimplification'), value: integrityChecks?.get('alarms_without_simplification', 'N/A').toString() },
    { label: t('maintenance.alarmsWithoutDevice'), value: integrityChecks?.get('alarms_without_device', 'N/A').toString()  },
    { label: t('maintenance.circuitsWithoutUnit'), value: integrityChecks?.get('circuits_without_unit', 'N/A').toString()  },
    { label: t('maintenance.duplicateCircuitTags'), value: integrityChecks?.get('duplicate_circuit_tags', 'N/A').toString()  },
    { label: t('maintenance.circuitsWithoutController'), value: integrityChecks?.get('circuits_without_controller', 'N/A').toString()  },
    { label: t('maintenance.controllersWithoutUnit'), value: integrityChecks?.get('controllers_without_unit', 'N/A').toString()  },
    { label: t('maintenance.notesWithoutDevice'), value: integrityChecks?.get('notes_without_device', 'N/A').toString() },
    { label: t('maintenance.ticketsWithoutDevice', { label: pluralWorkTicketLabel || t('workTickets.title') }), value: integrityChecks?.get('sap_notifications_without_device', 'N/A').toString() },
  ];

  const raisedAtSortFunction = (_a, _b, sort) => {
    const _aMostRecentDate = _a.get('alarms').maxBy(alarm => new Date(alarm.get('alarm_raised_at')).getTime());
    const _bMostRecentDate = _b.get('alarms').maxBy(alarm => new Date(alarm.get('alarm_raised_at')).getTime());

    const a = _aMostRecentDate?.get('alarm_raised_at') ?? null;
    const b = _bMostRecentDate?.get('alarm_raised_at') ?? null;

    if (a && b) {
      return (a < b) ? sort : sort*-1;
    } else if (!a) {
      return sort*-1;
    } else {
      return sort*1;
    }
  };

  const criticalAlarmsAscSortFunction = (_a, _b) => {
    const a = _a.getIn(['alarms', 0, 'alarm_group']);
    const b = _b.getIn(['alarms', 0, 'alarm_group']);

    if (a && b) {
      return (a < b) ? 1 : -1;
    } else if (!a) {
      return -1;
    } else {
      return 1;
    }
  };

  const criticalAlarmsDescSortFunction = (_a, _b) => {
    const a = _a.getIn(['alarms', 0, 'alarm_group']);
    const b = _b.getIn(['alarms', 0, 'alarm_group']);

    if (a && b) {
      return (a < b) ? 1 : -1;
    } else if (!b) {
      return -1;
    } else {
      return 1;
    }
  };

  const htcAscSortFunction = (a, b) => {
    return parseInt(a.get('heat_trace_circuit_num')) > parseInt(b.get('heat_trace_circuit_num')) ? 1 : -1;
  };

  const htcDescSortFunction = (a, b) => {
    return parseInt(a.get('heat_trace_circuit_num')) < parseInt(b.get('heat_trace_circuit_num')) ? 1 : -1;
  };

  const deviceTagAscSortFunction = (a, b) => {
    return (a.get('controller_tag' ) || a.get('raw_tag')) > (b.get('controller_tag' ) || b.get('raw_tag')) ? 1 : -1;
  };

  const deviceTagDescSortFunction = (a, b) => {
    return (a.get('controller_tag' ) || a.get('raw_tag')) < (b.get('controller_tag' ) || b.get('raw_tag')) ? 1 : -1;
  };

  const makeToggleAlarmGroup = (criticality) => {
    return () => {
      setAlarmGroupCollapsed(alarmGroupCollapsed.set(criticality, !alarmGroupCollapsed.get(criticality)));
    };
  };

  // Multi-select handlers
  const getSelectedForCriticality = (criticalityValue: number): Set<string> => {
    return selectedRowsRef.current.get(criticalityValue) || Set();
  };

  const handleRowSelect = (criticalityValue: number, deviceId: string) => {
    const current = selectedRowsRef.current;
    const currentSelected = current.get(criticalityValue) || Set();
    if (currentSelected.has(deviceId)) {
      setSelectedRows(current.set(criticalityValue, currentSelected.delete(deviceId)));
    } else {
      setSelectedRows(current.set(criticalityValue, currentSelected.add(deviceId)));
    }
  };

  const handleSelectAll = (criticalityValue: number, devices: any) => {
    const current = selectedRowsRef.current;
    const currentSelected = current.get(criticalityValue) || Set();
    const validDeviceIds = devices
      ?.filter((d: any) => !d.get('invalidated'))
      .map((d: any) => d.get('device_id'))
      .toSet() || Set();

    if (currentSelected.size === validDeviceIds.size) {
      // Deselect all
      setSelectedRows(current.set(criticalityValue, Set()));
    } else {
      // Select all
      setSelectedRows(current.set(criticalityValue, validDeviceIds));
    }
  };

  const getTotalSelectedCount = (): number => {
    let count = 0;
    selectedRows.forEach((selected) => {
      count += selected.size;
    });
    return count;
  };

  const getSelectedDevices = () => {
    const devices: any[] = [];
    selectedRows.forEach((selectedDeviceIds, criticalityValue) => {
      const devicesForCriticality: any = devicesInAlarmWithoutNotificationByCriticality.get(criticalityValue as number);
      if (devicesForCriticality) {
        devicesForCriticality.forEach((device: any) => {
          if (selectedDeviceIds.has(device.get('device_id'))) {
            devices.push(device);
          }
        });
      }
    });
    return List(devices);
  };

  const toggleBulkValidateModal = () => {
    setBulkValidateModalOpen(!bulkValidateModalOpen);
  };

  const handleBulkValidateSubmit = (notificationNumber: string, description: string) => {
    const devices = getSelectedDevices();
    const alarmIds: string[] = [];
    const deviceIds: string[] = [];

    devices.forEach((device: any) => {
      const deviceId = device.get('device_id');
      deviceIds.push(deviceId);
      device.get('alarms').forEach((alarm: any) => {
        alarmIds.push(alarm.get('id'));
      });
    });

    dispatch(validateAlarms(notificationNumber, alarmIds, description, null, selectedFilters, deviceIds));
    toggleBulkValidateModal();
    setSelectedRows(Map()); // Clear all selections
    setHasActionOccurred(true);
  };

  const formatAlarmCell = (alarm) => {
    const noteExpiredAt = alarm.get('note_expired_at');
    const label = formatAlarmLabel(alarm.get('description'), t);

    return (
      noteExpiredAt ? (
        <Tooltip title='Note Expired' className='warning-text'>
          <div className='cell-with-icon'><span className='icon'><AlertIcon width={16} height={16} /></span><span className='label'>{label}</span></div>
        </Tooltip>
      ) : label
    );
  };

  useEffect(() => {
    dispatch(setSettingsModalClose(null));
  }, []);

  const makeToggleDismissAlarmsGroupModal = () => {
    return () => {
      toggleDismissAlarmsGroupModal();
    };
  };

  const toggleDismissAlarmsGroupModal = () => {
    if (!settingsModal?.get('isOpen')) {
      dispatch(setSettingsModalOpen(null, SettingsModalTypes.DISMISS_ALARM_GROUP));
    }
    else {
      dispatch(setSettingsModalClose(null));
    }
  };
  const handleDismissAlarmsGroupSubmit = (rec: any) => {
    dispatch(dismissAlarmsByGroup(rec.get('alarmGroup'), criticality, selectedFilters));
    setHasActionOccurred(true);
  };

  const alarmGroupsArr = [], alarmGroupOpts = [];

  const openAlarmGroupDismissalModal = (critical) => {
    setCriticality(critical);
    const records: any = devicesInAlarmWithoutNotificationByCriticality.get(critical);

    // building alarm group options array for settings modal to show in drop down
    records?.map(record => {
      const alarmsList = record.get('alarms');
      alarmsList?.map(alarm => {
        if (alarm.get('alarm_group') && !alarmGroupsArr.includes(alarm.get('alarm_group'))) {
          alarmGroupsArr.push(alarm.get('alarm_group'));
        }
      });
    });
    alarmGroupsArr?.map(group => alarmGroupOpts.push({ label: formatAlarmLabel(group, t), value: group }));
    setAlarmGroupOptions(alarmGroupOpts);
    toggleDismissAlarmsGroupModal();
  };
  const criticalityOptions = [];
  for (let i=0; i < criticalityLabels.size; i++) {
    criticalityOptions.push({ label: criticalityLabels.get(i), value: `${i}` });
  }

  useEffect(() => {
    criticalities.map( x => {
      if (x.value === criticality) {
        setCriticalityLabel(x.label);
      }
    });
  }, [criticality]);

  useEffect(() => {
    if (hasActionOccurred && criticalAlarmsWithoutNotification === 0 && isAllFiltersEmpty && !useAlarmReportsLoading) {
      if (useAlarmReportsValue) {
        toast.success(<div>Nice work! All critical alarms have been validated. Now just send the report!
          <NavLink className='finishReport' to={'report/send'} > Finish Report </NavLink></div>, {
          autoClose: false,
        });
      } else {
        toast.success(t('maintenance.allCriticalValidated'));
      }
      // Reset hasActionOccurred after showing the message to prevent it from showing again
      setHasActionOccurred(false);
    }
  }, [hasActionOccurred, criticalAlarmsWithoutNotification, isAllFiltersEmpty, useAlarmReportsLoading, useAlarmReportsValue]);
  const makeColumns = (criticalityValue: number, devices: any) => {
    const cols: any[] = [];

    // Checkbox column for selection - modern styled
    const checkboxStyles = {
      padding: '8px',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      '& .MuiSvgIcon-root': {
        fontSize: 22,
        borderRadius: '6px',
      },
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
      '&.Mui-checked': {
        color: '#1976d2',
        '& .MuiSvgIcon-root': {
          filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3))',
        },
      },
      '&.MuiCheckbox-indeterminate': {
        color: '#1976d2',
      },
    };

    cols.push({
      id: 'select',
      Header: () => {
        const currentSelected = getSelectedForCriticality(criticalityValue);
        const validDevs = devices?.filter((d: any) => !d.get('invalidated')) || List();
        const isAllSelected = validDevs.size > 0 && currentSelected.size === validDevs.size;
        const isSomeSelected = currentSelected.size > 0 && currentSelected.size < validDevs.size;
        return (
          <PermissionFence can={['edit-sap-notification', 'edit-device']}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isSomeSelected}
              onChange={() => handleSelectAll(criticalityValue, devices)}
              sx={checkboxStyles}
            />
          </PermissionFence>
        );
      },
      accessor: (row: any) => row.get('device_id'),
      width: '56px',
      disableSortBy: true,
      Cell: (row: any) => {
        const isInvalidated = row.cell.row.original.get('invalidated');
        const deviceId = row.cell.value;
        if (isInvalidated) return null;
        const isChecked = getSelectedForCriticality(criticalityValue).has(deviceId);
        return (
          <PermissionFence can={['edit-sap-notification', 'edit-device']}>
            <Checkbox
              checked={isChecked}
              onChange={() => handleRowSelect(criticalityValue, deviceId)}
              sx={checkboxStyles}
              onClick={(e) => e.stopPropagation()}
            />
          </PermissionFence>
        );
      }
    });

    cols.push({
      id: 'tag',
      Header: t('maintenance.deviceTag'),
      accessor: (row: any) => row.get('circuit_tag'),
      sortFunction: deviceTagAscSortFunction,
      sortDescFunction: deviceTagDescSortFunction,
      maxWidth: '200px',
      Cell: (row: any) => { return (
        <DeviceLinkCombo device={row.cell.row.original} />
      ); },
      ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
    });

    cols.push(
      {
        id: 'heat_trace_circuit_num',
        Header: t('maintenance.htc'),
        width: '80px',
        accessor: (row: any) => row.get('heat_trace_circuit_num'),
        sortFunction: htcAscSortFunction,
        sortDescFunction: htcDescSortFunction,
        ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
      },
      {
        id: 'alarms',
        Header: t('maintenance.criticalAlarms'),
        accessor: (row: any) => row.get('alarms'),
        minWidth: '220px',
        sortFunction: criticalAlarmsAscSortFunction,
        sortDescFunction: criticalAlarmsDescSortFunction,
        Cell: (row: any) => row.cell.value.map((alarm, index) => (<div key={index} className='alarm-list'>{formatAlarmCell(alarm)}</div>)),
        ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
      },
      {
        id: 'temperature',
        Header: t('maintenance.alarmTemp'),
        accessor: (row: any) => row.get('alarms'),
        Cell: (row: any) => row.cell.value.map((alarm, index) => (<div key={index} className='alarm-list'>{temperatureUnit === 'F' && alarm.get('actual_temperature') ? celsiusToFahrenheitConvertor(alarm.get('actual_temperature')) : alarm.get('actual_temperature')}</div>)),
        ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
      },
      {
        id: 'current',
        Header: t('maintenance.alarmCurrent'),
        accessor: (row: any) => row.get('alarms'),
        Cell: (row: any) => { return row.cell.value.map((alarm, index) => (<div key={index} className='alarm-list'>{alarm.get('actual_current')}</div>)); },
        ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
      },
      {
        id: 'setting',
        Header: t('maintenance.setting'),
        accessor: (row: any) => row.get('alarms'),
        Cell: (row: any) => { return row.cell.value.map((alarm, index) => {
          const alarmName = alarm?.get('alarm');
          let value = alarm.get('setting');
          if (alarmName?.includes('temp')) {
            if (temperatureUnit === 'F' && alarm.get('setting')) {
              value = celsiusToFahrenheitConvertor(alarm.get('setting'));
            }
          }
          return (<div key={index} className='alarm-list'>{value}</div>);
        }); },
        ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
      },
      {
        id: 'real_temperature',
        Header: t('maintenance.actualTemp'),
        disableSortBy: true,
        accessor: (row: any) => row.get('alarms'),
        Cell: (row: any) => {
          let realTempRendered = false;
          return row.cell.value.map((alarm, index) => {
            const alarmName = alarm?.get('alarm');
            let realTemp = alarm.get('real_temperature');
            if (!alarmName) {
              return 'N/A';
            } else if (alarmName?.includes('temp') && !realTempRendered) {
              realTemp = realTemp && temperatureUnit === 'F' ? celsiusToFahrenheitConvertor(realTemp) : realTemp;
              realTempRendered = true;
              return (<div key={index} className='alarm-list'>{realTemp}</div>);
            }
          }); },
        ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
      },
      {
        id: 'real_current',
        Header: t('maintenance.actualCurrent'),
        disableSortBy: true,
        accessor: (row: any) => row.get('alarms'),
        Cell: (row: any) => {
          let realCurrentRendered = false;
          return row.cell.value.map((alarm, index) => {
            const alarmName = alarm?.get('alarm');
            const description = alarm?.get('description');
            const realCurrent = alarm.get('real_current');
            if (!alarmName) {
              return 'N/A';
            } else if (description?.includes('Load Current') && !realCurrentRendered) {
              realCurrentRendered = true;
              return (<div key={index} className='alarm-list'>{realCurrent}</div>);
            }
          }); },
        ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
      },
      {
        id: 'alarm_raised_at',
        Header: t('maintenance.alarmRaisedAt'),
        accessor: (row: any) => row.get('alarms'),
        sortFunction: (v1, v2) => raisedAtSortFunction(v1, v2, 1),
        sortDescFunction: (v1, v2) => raisedAtSortFunction(v1, v2, -1),
        Cell: (row: any) => { return row.cell.value.map((alarm, index) => (<div key={index} className='alarm-list'>{formatDate(alarm.get('alarm_raised_at'))}</div>)); },
        ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : '',
        minWidth: '220px'
      },
      {
        Header: ' ',
        accessor: (row: any) => row.get('index'),
        maxWidth: '60px',
        disableSortBy: true,
        Cell: (row: any) => row.cell.row.original.get('invalidated') ?
          <div className='maintenance-actions'>
            <PermissionFence can={['edit-eht-note', 'edit-device']}>
              <Button onClick={() => undo(row.cell.value, row.cell.row.original.get('alarms'), row.cell.row.original.get('controller_id'), row.cell.row.original.get('circuit_id'))} icon={<UndoIcon style={{ fontSize: 14 }} />} />
            </PermissionFence>
          </div>
          : (
            <div className='maintenance-actions'>
              <PermissionFence can={['edit-eht-note', 'edit-device']}>
                <Tooltip placement='top' title={t('maintenance.validate')}>
                  <span style={{ display: 'inline-block' }}>
                    <Button onClick={makeToggleAlarmNoteModal(row.cell.value, row.cell.row.original.get('alarms'), row.cell.row.original.get('controller_id'), row.cell.row.original.get('circuit_id'), row.cell.row.original.get('device_id'))} icon={<ValidateIcon style={{ fontSize: 14 }} />} />
                  </span>
                </Tooltip>
              </PermissionFence>
              <PermissionFence can={['edit-sap-notification', 'edit-device']}>
                <Tooltip placement='top' title={t('maintenance.addWorkTicket')}>
                  <span style={{ display: 'inline-block' }}>
                    <Button onClick={() => toggleNotificationModal(row.cell.value, row.cell.row.original.get('alarms'),  row.cell.row.original.get('device_id'))} icon={<CheckCircleIcon style={{ fontSize: 14 }} />} />
                  </span>
                </Tooltip>
              </PermissionFence>
            </div>
          )
      }
    );

    return cols;
  };

  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';

  return (
    <div className='page' id='maintenance'>
      <Helmet>
        <title>{title} - {t('maintenance.title')}</title>
      </Helmet>
      <SettingsModal
        open={settingsModal?.get('isOpen') && settingsModal?.get('id') === SettingsModalTypes.DISMISS_ALARM_GROUP}
        handleClose={makeToggleDismissAlarmsGroupModal()}
        handleSubmit={handleDismissAlarmsGroupSubmit}
        title={t('maintenance.batchResetAlarmsByGroup', { label: criticalitylabel })}
        loading={settingsModal?.get('loading')}
        fields={
          fromJS({
            alarmGroup: {
              type: 'select',
              label: t('maintenance.alarmGroup'),
              options: alarmGroupOptions,
              errorText: t('common.required'),
            }
          })
        }
      />

      {(notificationModalOpen) ?
        <NotificationNumberModal open={notificationModalOpen} handleClose={() => toggleNotificationModal()} handleSubmit={handleNotificationSubmit()} /> : ''}
      {(bulkValidateModalOpen) ?
        <BulkValidateModal
          open={bulkValidateModalOpen}
          handleClose={toggleBulkValidateModal}
          handleSubmit={handleBulkValidateSubmit}
          selectedDevices={getSelectedDevices()}
        /> : ''}
      {(alarmNoteModalOpen) ?
        <AlarmNoteModal open={alarmNoteModalOpen} alarms={alarms} circuitId={circuitId} controllerId={controllerId} deviceId={deviceId} handleClose={makeToggleAlarmNoteModal()} handleSubmit={handleAlarmNote()} /> : ''}
      <Grid container spacing={2}>

        <Grid item xs={12} md={12}>
          <Paper>
            <UnitSelector />
          </Paper>
        </Grid>


        <Grid item xs={12} md={6} lg={6}>
          <Paper>
            <h2>{t('maintenance.circuitStatusSummary')}</h2>
            <DataTable
              columns={[
                {
                  id: 'circuit-status-label',
                  Header: '',
                  accessor: row => row.label,
                },
                {
                  id: 'circuit-status-value',
                  Header: '',
                  accessor: row => row.value,
                  width: '54px'
                }
              ]}
              data={List(noteSummaryData)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <Paper>
            <h2>{t('maintenance.integrityChecks')}</h2>
            <DataTable
              columns={[
                {
                  id: 'integrity-checks-label',
                  Header: '',
                  accessor: row => row.label,
                },
                {
                  id: 'integrity-checks-value',
                  Header: '',
                  accessor: row => row.value ?? 'N/A',
                  width: '80px'
                }
              ]}
              data={List(integrityChecksData)}
            />
          </Paper>
        </Grid>

        <div className='maintenance-title-bar'>
          <h2>{t('maintenance.alarmsWithoutTicket', { label: singularWorkTicketLabel || t('workTickets.singular') })}</h2>
        </div>
        {loading ?
          <LoadingPlaceholder /> :
          criticalities.map((criticality, idx) => {
            const collapsed = alarmGroupCollapsed.get(criticality.value, false);
            return (
              <Grid key={idx} item xs={12} md={12} lg={12}>
                <Paper>
                  {showRotateMessage ? <><h3>{t('maintenance.priority')}: {criticality.label}</h3><DeviceRotate/></> :
                    <h3 className='expandable' onClick={makeToggleAlarmGroup(criticality.value)}>{t('maintenance.priority')}: {criticality.label}&nbsp;<span className='expand-arrow'>{collapsed ? '▲' : '▼'}</span></h3>}
                  {!collapsed && !showRotateMessage ? <>
                    <PermissionFence can={['edit-eht-note', 'edit-device']}>
                      {(devicesInAlarmWithoutNotificationByCriticality.get(criticality.value)) ? <Button cta className='group-dismiss-button' onClick={() => openAlarmGroupDismissalModal(criticality.value)}>{t('maintenance.batchResetAlarms')}</Button> : null}
                    </PermissionFence>
                    <DataTable
                      stickyHeader
                      columns={makeColumns(criticality.value, devicesInAlarmWithoutNotificationByCriticality.get(criticality.value))}
                      data={devicesInAlarmWithoutNotificationByCriticality.get(criticality.value) || List()}
                      pagination
                      paginationPerPage={25}
                      tableId={`maintenanceScreenTable${idx}`}
                      selectedRowId={`selection-${getSelectedForCriticality(criticality.value).size}`}
                    /></> : null
                  }
                </Paper>
              </Grid>
            );
          })
        }

      </Grid>

      {getTotalSelectedCount() > 0 && (
        <PermissionFence can={['edit-sap-notification', 'edit-device']}>
          <FloatingActionBar label={`${getTotalSelectedCount()} circuit${getTotalSelectedCount() !== 1 ? 's' : ''} selected`}>
            <Button className='clear-btn' onClick={() => setSelectedRows(Map())}>Clear</Button>
            <Button onClick={toggleBulkValidateModal} icon={<CheckCircleIcon style={{ fontSize: 14 }} />}>
              Validate Selected
            </Button>
          </FloatingActionBar>
        </PermissionFence>
      )}
    </div>
  );
}
